import React from "react";
import { 
  Stethoscope, 
  Cpu, 
  FlaskConical, 
  ShieldCheck, 
  ArrowRight,
  Zap,
  Activity,
  Globe,
  Database,
  Lock,
  Smartphone
} from "lucide-react";
import { motion } from "framer-motion";
import Navigation from "../components/Navigation";
import FooterSection from "../components/FooterSection";
import "./Partnerships.css";

const Partnerships = () => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1,
      transition: { staggerChildren: 0.1, delayChildren: 0.2 }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: { 
      y: 0, 
      opacity: 1, 
      transition: { duration: 0.6, ease: "easeOut" }
    }
  };

  const allianceTiers = [
    {
      id: "healthcare",
      title: "Healthcare Partners",
      subtitle: "For Clinics & Hospitals",
      icon: Stethoscope,
      color: "cyan",
      desc: "Connect your entire medical team. Use one dashboard to watch over all your patients safely.",
      benefits: [
        "Live Medical Dashboards",
        "Team Patient Tracking",
        "Instant Health Alerts",
        "Clear Group Reports"
      ]
    },
    {
      id: "iot",
      title: "Device Makers",
      icon: Cpu,
      subtitle: "For Wearable Tech",
      color: "green",
      desc: "Connect your smart health gadgets to Medico. Make it easy for people to see their data on our platform.",
      benefits: [
        "Easy Sensor Setup Tools",
        "Direct Device Syncing",
        "High Performance Links",
        "Locked Device Security"
      ]
    },
    {
      id: "research",
      title: "Health Research",
      icon: FlaskConical,
      subtitle: "For Data Scientists",
      color: "purple",
      desc: "Help us discover new health patterns. Use anonymous data to build better health tools for everyone.",
      benefits: [
        "Private Data Access",
        "AI Tool Integration",
        "Health Trend Studies",
        "Pattern Discovery Labs"
      ]
    }
  ];

  return (
    <div className="partner-portal">
      <Navigation />
      
      {/* Visual Overlays */}
      <div className="partner-hex-grid"></div>
      <div className="partner-scanline"></div>

      <motion.main 
        className="partner-content"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        
        {/* HERO SECTION */}
        <section className="partner-hero">
          <motion.div className="partner-hud-badge" variants={itemVariants}>
            <Activity size={14} />
            <span>ALLIANCE_PROGRAM_v2.5</span>
          </motion.div>

          <motion.h1 variants={itemVariants}>
            Work With <span className="partner-highlight">Medico</span>
          </motion.h1>

          <motion.p variants={itemVariants}>
            We build simple tools to help people stay healthy. Join our group of doctors, 
            tech makers, and researchers to make healthcare better for everyone.
          </motion.p>
        </section>

        {/* ALLIANCE TIERS */}
        <section className="partner-tiers-sector">
          <div className="partner-tiers-grid">
            {allianceTiers.map((tier) => {
              const Icon = tier.icon;
              return (
                <motion.div 
                  key={tier.id} 
                  className={`partner-card ${tier.color}`}
                  variants={itemVariants}
                >
                  <div className="partner-card-icon">
                    <Icon size={48} />
                  </div>
                  <h3>{tier.title}</h3>
                  <div style={{fontSize: '0.8rem', color: 'currentColor', marginBottom: '1rem', letterSpacing: '1px', fontWeight: '800'}}>
                    {tier.subtitle}
                  </div>
                  <p>{tier.desc}</p>
                  
                  <ul className="partner-benefits-list">
                    {tier.benefits.map((benefit, i) => (
                      <li key={i} className="partner-benefit-item">
                        <div className="partner-bullet"></div>
                        {benefit}
                      </li>
                    ))}
                  </ul>
                </motion.div>
              );
            })}
          </div>
        </section>

        {/* ALLIANCE ENTRY NODE */}
        <section className="partner-cta-sector">
          <motion.div className="partner-cta-node partner-glass" variants={itemVariants}>
            <div className="partner-hud-badge" style={{color: 'var(--partner-accent-orange)', borderColor: 'rgba(245, 158, 11, 0.3)'}}>
              <ShieldCheck size={14} />
              <span>SECURE_ALLIANCE_ENTRY</span>
            </div>
            <h2>Start Your Alliance</h2>
            <p>
              Looking to join the Medico ecosystem? Tell us about your project or team 
              and let's see how we can help people together.
            </p>
            <button className="partner-pulse-btn">
              Submit Proposal
            </button>
            <div style={{marginTop: '2rem', fontSize: '0.8rem', color: 'var(--partner-text-dim)'}}>
              IDENTITY_VERIFICATION_REQUIRED_FOR_ONBOARDING
            </div>
          </motion.div>
        </section>

      </motion.main>

      <FooterSection />
    </div>
  );
};

export default Partnerships;